import React from 'react';

const LoadingSpinner: React.FC = () => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0, // Full page coverage
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999, // Above everything including nav bar
      flexDirection: 'column' as const,
      backdropFilter: 'blur(10px)'
    }}>
      {/* Animated SVG Loader */}
      <svg 
        width="120" 
        height="120" 
        viewBox="0 0 240 240"
        style={{
          marginBottom: '20px',
          filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))'
        }}
      >
        <circle 
          cx="120" 
          cy="120" 
          r="105" 
          fill="none" 
          stroke="#667eea" 
          strokeWidth="20" 
          strokeDasharray="0 660" 
          strokeDashoffset="-330" 
          strokeLinecap="round"
          style={{
            animation: 'ringA 2s linear infinite'
          }}
        />
        <circle 
          cx="120" 
          cy="120" 
          r="35" 
          fill="none" 
          stroke="#10b981" 
          strokeWidth="20" 
          strokeDasharray="0 220" 
          strokeDashoffset="-110" 
          strokeLinecap="round"
          style={{
            animation: 'ringB 2s linear infinite'
          }}
        />
        <circle 
          cx="85" 
          cy="120" 
          r="70" 
          fill="none" 
          stroke="#f59e0b" 
          strokeWidth="20" 
          strokeDasharray="0 440" 
          strokeLinecap="round"
          style={{
            animation: 'ringC 2s linear infinite'
          }}
        />
        <circle 
          cx="155" 
          cy="120" 
          r="70" 
          fill="none" 
          stroke="#ef4444" 
          strokeWidth="20" 
          strokeDasharray="0 440" 
          strokeLinecap="round"
          style={{
            animation: 'ringD 2s linear infinite'
          }}
        />
      </svg>
      
      {/* Loading Text */}
      <div style={{
        color: '#374151',
        fontSize: '18px',
        fontWeight: '600',
        textAlign: 'center',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
      }}>
        Loading...
      </div>
      
      {/* Subtle pulse animation */}
      <div style={{
        width: '40px',
        height: '4px',
        backgroundColor: '#e5e7eb',
        borderRadius: '2px',
        marginTop: '20px',
        overflow: 'hidden',
        position: 'relative'
      }}>
        <div style={{
          width: '40px',
          height: '100%',
          backgroundColor: '#667eea',
          borderRadius: '2px',
          animation: 'loadingPulse 1.5s ease-in-out infinite',
          transform: 'translateX(-100%)'
        }} />
      </div>
      
      {/* Inline CSS for animations */}
      <style>{`
        @keyframes ringA {
          from, 4% {
            stroke-dasharray: 0 660;
            stroke-width: 20;
            stroke-dashoffset: -330;
          }
          12% {
            stroke-dasharray: 60 600;
            stroke-width: 30;
            stroke-dashoffset: -335;
          }
          32% {
            stroke-dasharray: 60 600;
            stroke-width: 30;
            stroke-dashoffset: -595;
          }
          40%, 54% {
            stroke-dasharray: 0 660;
            stroke-width: 20;
            stroke-dashoffset: -660;
          }
          62% {
            stroke-dasharray: 60 600;
            stroke-width: 30;
            stroke-dashoffset: -665;
          }
          82% {
            stroke-dasharray: 60 600;
            stroke-width: 30;
            stroke-dashoffset: -925;
          }
          90%, to {
            stroke-dasharray: 0 660;
            stroke-width: 20;
            stroke-dashoffset: -990;
          }
        }
        
        @keyframes ringB {
          from, 12% {
            stroke-dasharray: 0 220;
            stroke-width: 20;
            stroke-dashoffset: -110;
          }
          20% {
            stroke-dasharray: 20 200;
            stroke-width: 30;
            stroke-dashoffset: -115;
          }
          40% {
            stroke-dasharray: 20 200;
            stroke-width: 30;
            stroke-dashoffset: -195;
          }
          48%, 62% {
            stroke-dasharray: 0 220;
            stroke-width: 20;
            stroke-dashoffset: -220;
          }
          70% {
            stroke-dasharray: 20 200;
            stroke-width: 30;
            stroke-dashoffset: -225;
          }
          90% {
            stroke-dasharray: 20 200;
            stroke-width: 30;
            stroke-dashoffset: -305;
          }
          98%, to {
            stroke-dasharray: 0 220;
            stroke-width: 20;
            stroke-dashoffset: -330;
          }
        }
        
        @keyframes ringC {
          from {
            stroke-dasharray: 0 440;
            stroke-width: 20;
            stroke-dashoffset: 0;
          }
          8% {
            stroke-dasharray: 40 400;
            stroke-width: 30;
            stroke-dashoffset: -5;
          }
          28% {
            stroke-dasharray: 40 400;
            stroke-width: 30;
            stroke-dashoffset: -175;
          }
          36%, 58% {
            stroke-dasharray: 0 440;
            stroke-width: 20;
            stroke-dashoffset: -220;
          }
          66% {
            stroke-dasharray: 40 400;
            stroke-width: 30;
            stroke-dashoffset: -225;
          }
          86% {
            stroke-dasharray: 40 400;
            stroke-width: 30;
            stroke-dashoffset: -395;
          }
          94%, to {
            stroke-dasharray: 0 440;
            stroke-width: 20;
            stroke-dashoffset: -440;
          }
        }
        
        @keyframes ringD {
          from, 8% {
            stroke-dasharray: 0 440;
            stroke-width: 20;
            stroke-dashoffset: 0;
          }
          16% {
            stroke-dasharray: 40 400;
            stroke-width: 30;
            stroke-dashoffset: -5;
          }
          36% {
            stroke-dasharray: 40 400;
            stroke-width: 30;
            stroke-dashoffset: -175;
          }
          44%, 50% {
            stroke-dasharray: 0 440;
            stroke-width: 20;
            stroke-dashoffset: -220;
          }
          58% {
            stroke-dasharray: 40 400;
            stroke-width: 30;
            stroke-dashoffset: -225;
          }
          78% {
            stroke-dasharray: 40 400;
            stroke-width: 30;
            stroke-dashoffset: -395;
          }
          86%, to {
            stroke-dasharray: 0 440;
            stroke-width: 20;
            stroke-dashoffset: -440;
          }
        }
        
        @keyframes loadingPulse {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(0%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

export default LoadingSpinner; 